import React, { useState, useEffect, useMemo } from "react";
import { Box, IconButton } from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import ReusableDatePicker from "./clender";
import dayjs, { Dayjs } from "dayjs";
import { colors } from "../../constants/colors";
import { useSelector } from "react-redux";

interface TripDateSelectorProps {
  availableDates: (string | Date)[];
  tripDate?: Dayjs;
  onChangeDate?: (value: Dayjs) => void;
  disabled?: boolean;
  showToolbar?: boolean;
  allowSameDay?: boolean;
}

const TripDateSelector = ({
  availableDates,
  tripDate,
  onChangeDate = () => { },
  disabled = false,
  showToolbar = true,
  allowSameDay = false,
}: TripDateSelectorProps) => {
  const [selectedDate, setSelectedDate] = useState<Dayjs>(tripDate || dayjs());
  const [previousDate, setPreviousDate] = useState<Dayjs | null>(null);
  const [nextDate, setNextDate] = useState<Dayjs | null>(null);
  const { userDetails } = useSelector((s: any) => s.auth.loginDetails);
  const [latestThreeMonthsDates, setLatestThreeMonthsDates] = useState<string[]>([]);
  const today = dayjs();

  const availableAllDate = useMemo(() => {
    return availableDates
      ?.map((date) => dayjs(date))
      .sort((a, b) => a.valueOf() - b.valueOf()) || [];
  }, [availableDates]);

  useEffect(() => {
    const currentIndex = availableAllDate.findIndex((date) =>
      date.isSame(selectedDate, "day")
    );
    setPreviousDate(currentIndex > 0 ? availableAllDate[currentIndex - 1] : null);
    setNextDate(currentIndex < availableAllDate.length - 1 ? availableAllDate[currentIndex + 1] : null);
  }, [selectedDate, availableAllDate]);

  const handleDateChange = (newValue: Dayjs) => {
    if (disabled) return;
    setSelectedDate(newValue);
    onChangeDate(newValue);
  };

  const handlePreviousDateClick = () => {
    if (disabled) return;
    if (previousDate) {
      setSelectedDate(previousDate);
      onChangeDate(previousDate);
    }
  };

  const handleNextDateClick = () => {
    if (disabled) return;
    if (nextDate) {
      setSelectedDate(nextDate);
      onChangeDate(nextDate);
    }
  };

  const smallDateBoxStyle = {
    border: "1px solid black",
    borderRadius: "6px",
    padding: "3px 5px",
    fontSize: "11px",
    color: "black",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
  };

  const selectedDateStyle = {
    border: "2px solid orange",
    borderRadius: "6px",
    padding: "3px 5px",
    fontSize: "11px",
    color: "black",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
  };

  useEffect(() => {
    if (!userDetails?.[0]?.fleet_videos_upto || availableDates.length === 0) return;
    const monthsBack = parseInt(userDetails[0].fleet_videos_upto) / 30;
    const threeMonthsAgo = dayjs().subtract(monthsBack, "month").startOf("day");

    const filteredDates: any = availableDates.filter(dateStr =>
      dayjs(dateStr).isAfter(threeMonthsAgo)
    );

    setLatestThreeMonthsDates(filteredDates);

    if (filteredDates.length > 0) {
      const lastDateStr = filteredDates[filteredDates.length - 1];
      setSelectedDate(dayjs(lastDateStr));
    }
  }, [userDetails, availableDates]);

  if (!showToolbar) {
    // Header usage: clicking the date filter should open the calendar directly
    return (
      <Box
        sx={{
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? "none" : "auto",
        }}
      >
        <ReusableDatePicker
          autoOpen
          hideIcon
          selectedDate={selectedDate.toDate()}
          onDateChange={(date: any) => handleDateChange(dayjs(date))}
          availableDates={availableDates.map((d) => dayjs(d).toDate())}
          allowSameDay={allowSameDay}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0px 10px",
        float: "right",
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      <p
        style={{
          color: colors.black,
          fontSize: "14px",
          fontWeight: "500",
          paddingRight: "5px",
          marginBottom: "5px",
        }}
      >
        Date:
      </p>

      <IconButton
        onClick={handlePreviousDateClick}
        sx={{ color: "black" }}
        disabled={!previousDate || disabled}
      >
        <Box
          sx={{
            ...smallDateBoxStyle,
            border: `1px solid ${previousDate && previousDate.isBefore(today) ? colors.black : colors.silver
              }`,
            color: previousDate && previousDate.isBefore(today) ? colors.black : colors.silver,
          }}
        >
          <ArrowBack sx={{ marginRight: "3px", fontSize: "14px" }} />
          {previousDate ? previousDate.format("DD-MM") : ""}
        </Box>
      </IconButton>

      <Box sx={selectedDateStyle}>
        {selectedDate ? selectedDate.format("DD-MM-YYYY") : ""}
      </Box>

      <IconButton
        onClick={handleNextDateClick}
        sx={{ color: "black" }}
        disabled={!nextDate || nextDate.isAfter(today) || disabled}
      >
        <Box
          sx={{
            ...smallDateBoxStyle,
            border: `1px solid ${nextDate && nextDate.isBefore(today) ? colors.black : colors.silver
              }`,
            color: nextDate && nextDate.isBefore(today) ? colors.black : colors.silver,
          }}
        >
          {nextDate ? nextDate.format("DD-MM") : ""}
          <ArrowForward sx={{ marginLeft: "3px", fontSize: "14px" }} />
        </Box>
      </IconButton>

      <ReusableDatePicker
        autoOpen
        hideIcon
        selectedDate={selectedDate.toDate()}
        onDateChange={(date: any) => handleDateChange(dayjs(date))}
        availableDates={availableDates.map((d) => dayjs(d).toDate())}
        allowSameDay={allowSameDay}
      />
    </Box>
  );
};

export default TripDateSelector;

// import React, { useState, useEffect, useMemo } from "react";
// import { Box, IconButton } from "@mui/material";
// import { ArrowBack, ArrowForward } from "@mui/icons-material";
// import ReusableDatePicker from "./clender";
// import dayjs, { Dayjs } from "dayjs";
// import { colors } from "../../constants/colors";
// import { useSelector } from "react-redux";

// interface TripDateSelectorProps {
//   availableDates: (string | Date)[];
//   tripDate?: Dayjs;
//   onChangeDate?: (value: Dayjs) => void;
//   disabled?: boolean;
// }

// const TripDateSelector = ({
//   availableDates,
//   tripDate,
//   onChangeDate = () => { },
//   disabled = false,
// }: TripDateSelectorProps) => {
//   const [selectedDate, setSelectedDate] = useState<Dayjs>(tripDate || dayjs());
//   const [previousDate, setPreviousDate] = useState<Dayjs | null>(null);
//   const [nextDate, setNextDate] = useState<Dayjs | null>(null);
//   const { userDetails } = useSelector((s: any) => s.auth.loginDetails);
//   const [latestThreeMonthsDates, setLatestThreeMonthsDates] = useState<string[]>([]);
//   const today = dayjs();

//   const availableAllDate = useMemo(() => {
//     return availableDates
//       ?.map((date) => dayjs(date))
//       .sort((a, b) => a.valueOf() - b.valueOf()) || [];
//   }, [availableDates]);

//   useEffect(() => {
//     const currentIndex = availableAllDate.findIndex((date) =>
//       date.isSame(selectedDate, "day")
//     );
//     setPreviousDate(currentIndex > 0 ? availableAllDate[currentIndex - 1] : null);
//     setNextDate(currentIndex < availableAllDate.length - 1 ? availableAllDate[currentIndex + 1] : null);
//   }, [selectedDate, availableAllDate]);

//   const handleDateChange = (newValue: Dayjs) => {
//     if (disabled) return;
//     setSelectedDate(newValue);
//     onChangeDate(newValue);
//   };

//   const handlePreviousDateClick = () => {
//     if (disabled) return;
//     if (previousDate) {
//       setSelectedDate(previousDate);
//       onChangeDate(previousDate);
//     }
//   };

//   const handleNextDateClick = () => {
//     if (disabled) return;
//     if (nextDate) {
//       setSelectedDate(nextDate);
//       onChangeDate(nextDate);
//     }
//   };

//   const smallDateBoxStyle = {
//     border: "1px solid black",
//     borderRadius: "6px",
//     padding: "3px 5px",
//     fontSize: "11px",
//     color: "black",
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "center",
//     fontWeight: "bold",
//   };

//   const selectedDateStyle = {
//     border: "2px solid orange",
//     borderRadius: "6px",
//     padding: "3px 5px",
//     fontSize: "11px",
//     color: "black",
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "center",
//     fontWeight: "bold",
//   };

//   useEffect(() => {
//     if (!userDetails?.[0]?.fleet_videos_upto || availableDates.length === 0) return;
//     const monthsBack = parseInt(userDetails[0].fleet_videos_upto) / 30;
//     const threeMonthsAgo = dayjs().subtract(monthsBack, "month").startOf("day");

//     const filteredDates: any = availableDates.filter(dateStr =>
//       dayjs(dateStr).isAfter(threeMonthsAgo)
//     );

//     setLatestThreeMonthsDates(filteredDates);

//     if (filteredDates.length > 0) {
//       const lastDateStr = filteredDates[filteredDates.length - 1];
//       setSelectedDate(dayjs(lastDateStr));
//     }
//   }, [userDetails, availableDates]);

//   return (
//     <Box
//       sx={{
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//         padding: "0px 10px",
//         float: "right",
//         opacity: disabled ? 0.6 : 1,
//         pointerEvents: disabled ? "none" : "auto",
//       }}
//     >
//       <p
//         style={{
//           color: colors.black,
//           fontSize: "14px",
//           fontWeight: "500",
//           paddingRight: "5px",
//           marginBottom: "5px",
//         }}
//       >
//         Date:
//       </p>

//       <IconButton
//         onClick={handlePreviousDateClick}
//         sx={{ color: "black" }}
//         disabled={!previousDate || disabled}
//       >
//         <Box
//           sx={{
//             ...smallDateBoxStyle,
//             border: `1px solid ${previousDate && previousDate.isBefore(today) ? colors.black : colors.silver
//               }`,
//             color: previousDate && previousDate.isBefore(today) ? colors.black : colors.silver,
//           }}
//         >
//           <ArrowBack sx={{ marginRight: "3px", fontSize: "14px" }} />
//           {previousDate ? previousDate.format("DD-MM") : ""}
//         </Box>
//       </IconButton>

//       <Box sx={selectedDateStyle}>
//         {selectedDate ? selectedDate.format("DD-MM-YYYY") : ""}
//       </Box>

//       <IconButton
//         onClick={handleNextDateClick}
//         sx={{ color: "black" }}
//         disabled={!nextDate || nextDate.isAfter(today) || disabled}
//       >
//         <Box
//           sx={{
//             ...smallDateBoxStyle,
//             border: `1px solid ${nextDate && nextDate.isBefore(today) ? colors.black : colors.silver
//               }`,
//             color: nextDate && nextDate.isBefore(today) ? colors.black : colors.silver,
//           }}
//         >
//           {nextDate ? nextDate.format("DD-MM") : ""}
//           <ArrowForward sx={{ marginLeft: "3px", fontSize: "14px" }} />
//         </Box>
//       </IconButton>

//       <ReusableDatePicker
//         label="Select Trip Date"
//         selectedDate={selectedDate.toDate()}
//         onDateChange={(date: any) => handleDateChange(dayjs(date))}
//         availableDates={availableDates.map(d => dayjs(d).toDate())}
//       />
//     </Box>
//   );
// };

// export default TripDateSelector;